import { createReducer, on } from '@ngrx/store';
import * as AuthActions from './auth.actions';

type AsyncStatus = 'idle' | 'loading' | 'success' | 'error';

export interface AuthState {
  isLoggedIn: boolean;

  // email verification flow
  verifyEmailStatus: AsyncStatus;
  verifyEmailError?: string | null;

  // resend flow
  resendStatus: AsyncStatus;
  resendError?: string | null;
  resendCooldownUntil?: number | null;

  // (optional) generic auth error for login/register
  authError?: string | null;
  emailChangeStatus: AsyncStatus;
  emailChangeError: string | null;
  pendingEmail?: string | null; // where the confirmation was sent
  confirmChangeStatus: AsyncStatus;
  confirmChangeError: string | null;
  passwordResetRequestStatus: AsyncStatus;
  passwordResetRequestError: string | null;
  passwordResetStatus: AsyncStatus;
  passwordResetError: string | null;
}

export const initialState: AuthState = {
  isLoggedIn: false,
  verifyEmailStatus: 'idle',
  verifyEmailError: null,
  resendStatus: 'idle',
  resendError: null,
  resendCooldownUntil: null,
  authError: null,
  emailChangeStatus: 'idle',
  emailChangeError: null,
  pendingEmail: null,
  confirmChangeStatus: 'idle',
  confirmChangeError: null,
  passwordResetRequestStatus: 'idle',
  passwordResetRequestError: null,
  passwordResetStatus: 'idle',
  passwordResetError: null,
};

export const authReducer = createReducer<AuthState>(
  initialState,

  // LOGIN
  on(AuthActions.login, (state) => ({ ...state, isLoggedIn: false, authError: null })),
  on(AuthActions.loginSuccess, (state) => ({
    ...state,
    isLoggedIn: true,
    authError: null,
  })),
  on(AuthActions.loginFailure, (state, { error }) => ({
    ...state,
    isLoggedIn: false,
    authError: error ?? 'unknown',
  })),

  // REGISTER
  on(AuthActions.register, (state) => ({ ...state, isLoggedIn: false, authError: null })),
  on(AuthActions.registerSuccess, (state) => ({
    ...state,
    isLoggedIn: false,       // you redirect to verify now
    authError: null,
  })),
  on(AuthActions.registerFailure, (state, { error }) => ({
    ...state,
    isLoggedIn: false,
    authError: error ?? 'unknown',
  })),
  on(AuthActions.setLoggedIn, (state) => ({
    ...state,
    isLoggedIn: true,
  })),

  // VERIFY EMAIL
  on(AuthActions.verifyEmail, (state) => ({
    ...state,
    verifyEmailStatus: 'loading',
    verifyEmailError: null,
  })),
  on(AuthActions.verifyEmailSuccess, (state) => ({
    ...state,
    verifyEmailStatus: 'success',
    verifyEmailError: null,
  })),
  on(AuthActions.verifyEmailFailure, (state, { error }) => ({
    ...state,
    verifyEmailStatus: 'error',
    verifyEmailError: error ?? 'unknown',
  })),

  // RESEND
  on(AuthActions.resendVerificationEmail, (state) => ({
    ...state, resendStatus: 'loading', resendError: null
  })),
  on(AuthActions.resendVerificationEmailSuccess, (state) => ({
    ...state,
    resendStatus: 'success',
    resendError: null,
    resendCooldownUntil: Date.now() + 60_000,
  })),
  on(AuthActions.resendVerificationEmailFailure, (state, { error }) => ({
    ...state, resendStatus: 'error', resendError: error ?? 'unknown'
  })),
  on(AuthActions.loginEmailNotVerified, (state) => ({ ...state, authError: null })),
  // REQUEST
  on(AuthActions.requestEmailChange, (s) => ({
    ...s, emailChangeStatus: 'loading', emailChangeError: null
  })),
  on(AuthActions.requestEmailChangeSuccess, (s, { pendingEmail }) => ({
    ...s, emailChangeStatus: 'success', emailChangeError: null, pendingEmail
  })),
  on(AuthActions.requestEmailChangeFailure, (s, { error }) => ({
    ...s, emailChangeStatus: 'error', emailChangeError: error ?? 'unknown'
  })),
  // CONFIRM
  on(AuthActions.confirmEmailChange, (s) => ({
    ...s, confirmChangeStatus: 'loading', confirmChangeError: null
  })),
  on(AuthActions.confirmEmailChangeSuccess, (s) => ({
    ...s, confirmChangeStatus: 'success', confirmChangeError: null, pendingEmail: null
  })),
  on(AuthActions.confirmEmailChangeFailure, (s, { error }) => ({
    ...s, confirmChangeStatus: 'error', confirmChangeError: error ?? 'unknown'
  })),
  on(AuthActions.requestPasswordReset, (s) => ({
    ...s, passwordResetRequestStatus: 'loading', passwordResetRequestError: null
  })),
  on(AuthActions.requestPasswordResetSuccess, (s) => ({
    ...s, passwordResetRequestStatus: 'success'
  })),
  on(AuthActions.requestPasswordResetFailure, (s, { error }) => ({
    ...s, passwordResetRequestStatus: 'error', passwordResetRequestError: error ?? 'unknown'
  })),

  on(AuthActions.resetPassword, (s) => ({
    ...s, passwordResetStatus: 'loading', passwordResetError: null
  })),
  on(AuthActions.resetPasswordSuccess, (s) => ({
    ...s, passwordResetStatus: 'success'
  })),
  on(AuthActions.resetPasswordFailure, (s, { error }) => ({
    ...s, passwordResetStatus: 'error', passwordResetError: error ?? 'unknown'
  })),
  on(AuthActions.logout, () => initialState)
);
